import { useMemo, useState } from "react";
import { Field, reduxForm, SubmissionError } from "redux-form";
import { Button, Input } from "expano-components";

import { Dashboard, Select } from "components";

import useGet from "hooks/useGet";
import useUpdate from "hooks/useUpdate";

import origin_countries_options from "utils/select_options/origin_countries";
import { getKindLabel } from "utils/select_options/product_kind_options";

import CnCodeSection from "../../../components/CnCodeSection";

const InputField = (props) => <Input {...props} />;
const SelectField = (props) => <Select {...props} />;

const BasicData = ({
  product_group_id,
  kind,
  refetch,
  initialValues,
  ai_meta,
  handleSubmit,
  change,
  pristine,
  invalid,
}) => {
  const [producer_id, setProducerId] = useState(initialValues.producer_id);

  const { is_loading: is_loading_root_taxons, data: root_taxons } = useGet({
    query: ["root_taxons"],
    path: "/root_taxons",
    context: "list",
    search: { per_page: 10_000 },
  });

  const { is_loading: is_loading_producers, data: producers } = useGet({
    query: ["producers"],
    path: "/producers",
    context: "list",
    search: { per_page: 10_000 },
  });

  const { is_loading: is_loading_brands, data: brands } = useGet({
    query: ["brands", producer_id],
    path: "/brands",
    context: "list",
    search: {
      "q[producer_id_eq]": producer_id,
      per_page: 100,
    },
    options: {
      enabled: !!producer_id,
    },
  });

  const { is_updating, update } = useUpdate({
    path: `/product_groups/${product_group_id}`,
    onSuccess: refetch,
    onError: (err) => {
      throw new SubmissionError(
        Object.fromEntries(
          Object.entries(err).map(([key, value]) => {
            if (["producer", "brand", "root_taxon"].includes(key)) {
              return [`${key}_id`, value];
            }
            return [key, value];
          })
        )
      );
    },
  });

  const root_taxon_options = useMemo(() => {
    if (root_taxons?.length > 0) {
      return root_taxons.map(({ id, name, identifier }) => ({
        label: `${name} [${identifier}]`,
        value: id,
      }));
    }
    return [];
  }, [root_taxons]);

  const producer_options = useMemo(() => {
    if (producers?.length > 0) {
      return producers?.map(({ id, name }) => ({ label: name, value: id }));
    }
    return [];
  }, [producers]);

  const brand_options = useMemo(() => {
    if (brands?.length > 0) {
      return brands.map(({ id, name }) => ({ label: name, value: id }));
    }
    return [];
  }, [brands]);

  const onSubmit = (values) => update(values);

  return (
    <Dashboard
      header={{ title: "Podstawowe informacje", icon: "info" }}
      footer={
        <>
          <Button
            type="save"
            text={is_updating ? "Proszę czekać..." : "Zapisz"}
            disabled={pristine || invalid || is_updating}
            onClick={handleSubmit(onSubmit)}
          />
        </>
      }
    >
      <form
        className="dashboard__form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Field
          name="name"
          label="Nazwa"
          component={InputField}
          type="text"
        />
        <div className="row m-b-10">
          <div className="box-6">
            <Input
              label="Rodzaj"
              input={{
                name: "kind",
                disabled: true,
                value: getKindLabel(kind),
              }}
              meta={{}}
            />
          </div>
          <div className="box-6">
            <p className="additional-info m-t-20">
              Rodzaju nie można zmienić po utworzeniu.
            </p>
          </div>
        </div>
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              label="Kategoria"
              name="root_taxon_id"
              key={is_loading_root_taxons}
              component={SelectField}
              defaultValue={root_taxon_options?.find(
                ({ value }) => value === initialValues.root_taxon_id
              )}
              options={root_taxon_options}
              handleChange={(value) => {
                change("root_taxon_id", value);
              }}
            />
          </div>
          <div className="box-6">
            <Field name="sku" label="SKU" component={InputField} type="text" />
          </div>
        </div>
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              key={is_loading_producers}
              label="Producent"
              name="producer_id"
              component={SelectField}
              defaultValue={producer_options?.find(
                ({ value }) => value === initialValues.producer_id
              )}
              options={producer_options}
              handleChange={(value) => {
                change("producer_id", value);
                change("brand_id", null);
                setProducerId(value);
              }}
            />
          </div>
          <div className="box-6">
            <Field
              key={`${producer_id}_${is_loading_brands}`}
              label="Marka"
              name="brand_id"
              defaultValue={brand_options?.find(
                ({ value }) => value === initialValues.brand_id
              )}
              isDisabled={!producer_id}
              component={SelectField}
              options={brand_options}
              handleChange={(value) => {
                change("brand_id", value);
              }}
            />
          </div>
        </div>
        <p className="label m-b-10">
          Dane INTRASTAT (wymagane dla sprzedaży do UE)
        </p>
        <CnCodeSection
          change={change}
          product_group_id={product_group_id}
          ai_meta={ai_meta}
        />
        <div className="row m-b-10">
          <div className="box-6">
            <Field
              label="Kraj pochodzenia"
              name="country_of_origin"
              component={SelectField}
              defaultValue={origin_countries_options.find(
                ({ value }) => value === initialValues.country_of_origin
              )}
              options={origin_countries_options}
              handleChange={(value) => {
                change("country_of_origin", value);
              }}
            />
          </div>
        </div>
      </form>
    </Dashboard>
  );
};

const validate = (values) => {
  const errors = {};
  if (!values.name) {
    errors.name = "Nazwa produktu jest wymagana!";
  }

  if (!values.sku) {
    errors.sku = "SKU produktu jest wymagane!";
  }

  if (!values.root_taxon_id) {
    errors.root_taxon_id = "Kategoria jest wymagana!";
  }

  if (!values.producer_id) {
    errors.producer_id = "Producent jest wymagany!";
  }

  if (!values.brand_id) {
    errors.brand_id = "Marka jest wymagana!";
  }

  if (values.cn_code && !/^\d{8}$/.test(values.cn_code)) {
    errors.cn_code = "Kod CN musi składać się z 8 cyfr!";
  }

  return errors;
};

export default reduxForm({
  form: "update-product-group-basic",
  enableReinitialize: true,
  validate,
})(BasicData);
